/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {logCurrentThemeAccess} from './DLSThemeLogger';
import {currentTheme, effectiveThemeMode, multiThemeEnabled} from './ThemeStore';
import {DLSThemeModes, DLSThemes} from './types';

/**
 * Defines the shape of the object returned by the useThemeMode hook.
 */
export type UseThemeModeResults = {
  mode: DLSThemeModes;
  isDark: boolean;
};

/**
 * Defines the shape of the object returned by the useThemeContext hook.
 */
export type UseThemeContextResults = UseThemeModeResults & {
  theme: DLSThemes;
  isCurrent: boolean;
  isLegacy: boolean;
  multiThemeEnabled: boolean;
};

/**
 * Determines if the currently selected theme is the current theme; i.e.
 * the latest DLS version.
 *
 * ```typescript
 *  import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
 *
 *  if (isCurrentTheme()) {
 *    ...
 *  }
 * ```
 *
 * @returns `true` if the current theme is `Themes.current`, otherwise
 *          `false`.
 */
export const isCurrentTheme = (): boolean => (
  getCurrentTheme() === DLSThemes.current
);

/**
 * Determines if the currently selected theme is the legacy theme; i.e.
 * the old DLS version.
 *
 * ```typescript
 *  import {isLegacyTheme} from '@ion/dls/src/theme/useThemeContext';
 *
 *  if (isLegacyTheme()) {
 *    ...
 *  }
 * ```
 *
 * @returns `true` if the current theme is `Themes.legacy`, otherwise
 *          `false`.
 */
export const isLegacyTheme = (): boolean => (
  getCurrentTheme() === DLSThemes.legacy
);

/**
 * Determines if multi-theme switching is enabled.
 *
 * ```typescript
 *  import {isMultiThemeEnabled} from '@ion/dls/src/theme/useThemeContext';
 *
 *  if (isMultiThemeEnabled()) {
 *    ...
 *  }
 * ```
 *
 * @returns `true` if multi-theme switching is enabled, otherwise `false`.
 */
export const isMultiThemeEnabled = (): boolean => (
  multiThemeEnabled.value
);

/**
 * Returns the current theme identifier imperatively.
 *
 * Useful for imperative implementations where reactive updates
 * are not needed.
 *
 * ```typescript
 * import {getCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
 *
 * const theme = getCurrentTheme();
 * console.log(theme); // Themes.legacy or Themes.current
 * ```
 *
 * @returns The current theme identifier.
 */
export const getCurrentTheme = (): DLSThemes => {
  const theme = currentTheme.value;
  logCurrentThemeAccess(theme);
  return theme;
};

/**
 * Provides the effective theme mode (`mode`) with its `isDark` flag through a
 * single subscription to `effectiveThemeMode`. Tracks only the applied mode,
 * so the calling component re-renders when the effective mode changes, such
 * as a theme mode toggle or a gate of the effective mode opening or closing,
 * and not when another theme field changes. Use `useThemeContext` instead
 * when the component also reads the theme or the multi-theme flag.
 *
 * ```typescript
 * import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
 * ...
 *
 * const StatusDot = () => {
 *   const {isDark} = useThemeMode();
 *   return (
 *     <span className={isDark ? 'dot-dark' : 'dot-light'} />
 *   );
 * };
 * ```
 *
 * @returns An object containing the mode and isDark properties.
 */
export const useThemeMode = (): UseThemeModeResults => {
  const [mode, setMode] = useState(effectiveThemeMode?.value);

  useEffect(() => {
    const unsubscribeMode = effectiveThemeMode.subscribe(setMode);
    return () => {
      unsubscribeMode();
    };
  }, []);

  return {
    isDark : mode === DLSThemeModes.dark,
    mode
  };
};

/**
 * Provides access to the currently selected theme identifier and related
 * theme state information.
 *
 * Returns an object containing the current theme, boolean flags
 * indicating whether the current or legacy theme is active, and the
 * effective theme mode (`mode`) with its `isDark` flag. Updates
 * automatically when signal values change, including a theme mode change.
 *
 * ```typescript
 * import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
 *
 * const MyComponent = () => {
 *   const {theme, isCurrent, isLegacy, mode, isDark} = useThemeContext();
 *   return (
 *     <div>
 *       Current theme: {theme} ({mode})
 *       {isCurrent && <span>Using current theme</span>}
 *       {isDark && <span>Using dark mode</span>}
 *     </div>
 *   );
 * };
 * ```
 *
 * @returns An object containing theme, isCurrent, isLegacy, multiThemeEnabled,
 *          mode, and isDark properties.
 */
export const useThemeContext = (): UseThemeContextResults => {
  const [theme, setCurrentTheme] = useState(currentTheme?.value);
  const [isMultiThemeEnabled, setMultiThemeEnabled] = useState(multiThemeEnabled?.value);
  const {mode, isDark} = useThemeMode();

  useEffect(() => {
    const unsubscribeTheme = currentTheme.subscribe(setCurrentTheme);
    const unsubscribeMultiThemeEnabled = multiThemeEnabled.subscribe(setMultiThemeEnabled);
    return () => {
      unsubscribeTheme();
      unsubscribeMultiThemeEnabled();
    };
  }, []);

  return {
    multiThemeEnabled : isMultiThemeEnabled,
    isCurrent         : theme === DLSThemes.current,
    isLegacy          : theme === DLSThemes.legacy,
    theme,
    mode,
    isDark
  };
};

export default useThemeContext;
